import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import './Coachmark.css';

const STORAGE_PREFIX = 'tutorialSeen_';
const FIND_TIMEOUT_MS = 1500;

export const isTourSeen = (tourId) => {
  try { return localStorage.getItem(STORAGE_PREFIX + tourId) === '1'; } catch { return false; }
};

const markTourSeen = (tourId) => {
  try { localStorage.setItem(STORAGE_PREFIX + tourId, '1'); } catch { /* yoksay */ }
};

/**
 * İlk seferlik yönlendirme. steps: [{ selector, textKey }]
 * selector yoksa ipucu ekranın ortasında çıkar; selector bulunamazsa adım atlanır.
 * Hedefe tıklamak da bir sonraki adıma geçirir.
 */
const Coachmark = ({ tourId, steps, enabled = true }) => {
  const { t } = useTranslation('tutorial');
  const [seen, setSeen] = useState(() => isTourSeen(tourId));
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [ready, setReady] = useState(false);

  const active = enabled && !seen && index < steps.length;
  const step = active ? steps[index] : null;

  const finish = useCallback(() => {
    markTourSeen(tourId);
    setSeen(true);
  }, [tourId]);

  const next = useCallback(() => {
    if (index + 1 >= steps.length) finish();
    else setIndex((i) => i + 1);
  }, [index, steps.length, finish]);

  const nextRef = useRef(next);
  nextRef.current = next;
  const selector = step ? step.selector || '' : null;

  // Hedef elemanı bul ve konumunu takip et
  useLayoutEffect(() => {
    setReady(false);
    setRect(null);
    if (selector === null) return undefined;
    if (!selector) { setReady(true); return undefined; }

    let el = null;
    let cancelled = false;
    const startedAt = Date.now();

    const update = () => {
      if (cancelled) return;
      if (!el || !document.body.contains(el)) el = document.querySelector(selector);
      if (el) {
        const r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) {
          setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
          setReady(true);
          return;
        }
      }
      if (Date.now() - startedAt > FIND_TIMEOUT_MS) {
        cancelled = true;
        nextRef.current();
      }
    };

    update();
    const id = setInterval(update, 250);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);

    const onTargetClick = (e) => {
      if (el && el.contains(e.target)) nextRef.current();
    };
    document.addEventListener('click', onTargetClick, true);

    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      document.removeEventListener('click', onTargetClick, true);
    };
  }, [selector, index]);

  useEffect(() => {
    if (!enabled) setIndex(0);
  }, [enabled]);

  if (!step || !ready) return null;

  const pad = 6;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let bubbleStyle;
  if (rect) {
    const below = rect.top + rect.height + 140 < vh;
    const left = Math.max(12, Math.min(vw - 272, rect.left + rect.width / 2 - 130));
    bubbleStyle = below
      ? { top: rect.top + rect.height + pad + 12, left }
      : { top: Math.max(12, rect.top - pad - 12), left, transform: 'translateY(-100%)' };
  } else {
    bubbleStyle = { top: '30%', left: '50%', transform: 'translate(-50%, -50%)' };
  }

  // Portal olsa da React olayları üst bileşenlere kabarcıklanır; ipucuna tıklamak
  // popup kaplamasının "dışarı tıklayınca kapan" davranışını tetiklemesin.
  const stop = (e) => e.stopPropagation();

  return createPortal(
    <div
      className="coachmark-layer"
      aria-live="polite"
      onClick={stop}
      onMouseDown={stop}
      onMouseUp={stop}
      onPointerDown={stop}
      onPointerUp={stop}
      onTouchStart={stop}
      onTouchEnd={stop}
    >
      {rect && (
        <>
          <div
            className="coachmark-ring"
            style={{
              top: rect.top - pad,
              left: rect.left - pad,
              width: rect.width + pad * 2,
              height: rect.height + pad * 2,
            }}
          />
          <div
            className="coachmark-hand"
            style={{ top: rect.top + rect.height * 0.55, left: rect.left + rect.width * 0.6 }}
          >
            👆
          </div>
        </>
      )}
      <div className="coachmark-bubble" style={bubbleStyle} role="dialog">
        <p>{t(step.textKey)}</p>
        <div className="coachmark-actions">
          {steps.length > 1 && (
            <span className="coachmark-count">{index + 1}/{steps.length}</span>
          )}
          <button type="button" className="coachmark-skip" onClick={finish}>{t('skip')}</button>
          <button type="button" className="coachmark-next" onClick={next}>
            {index + 1 >= steps.length ? t('ok') : t('next')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Coachmark;
